import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { makeStyles } from '@material-ui/styles';
import { Tooltip } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(theme => ({
    icon: {
        fontSize: 15,
        marginLeft: theme.spacing(0.5),
        verticalAlign: 'text-bottom',
        color: theme.palette.info ? theme.palette.info.main : theme.palette.primary.main,
        cursor: 'help',
    },
    line: {
        whiteSpace: 'nowrap',
    },
}));

const formatDate = value => (value ? moment(value).format('DD.MM.YYYY') : '');

/**
 * (i) beside an athlete whose shown value has CHANGED since the competition —
 * a past competition shows the club, rank, coach… the athlete had then, and
 * the people who answer for the roster need to know that is not today's.
 *
 * `changes` is the API's `historicalChanges` entry for ONE athlete
 * (CompetitionParticipantNormalizer → HistoricalValueResolver::changedSince):
 * only the fields that differ, so an empty list renders nothing. The API
 * sends it to the administrator and the chief secretary only (organiser
 * decision, 3 Oct 2026); for everyone else there is nothing to render.
 *
 * Screens only — a printed protocol shows the values as they were, unmarked.
 */
const HistoricalChangeMark = ({ changes }) => {
    const classes = useStyles();
    const [t] = useTranslation();

    if (!changes || 0 === changes.length) {
        return null;
    }

    const none = t('competition.historicalChange.none');
    const title = (
        <div data-testid='historical-change-tooltip'>
            {changes.map(change => (
                <div key={change.field}>
                    <div className={classes.line}>
                        {t('competition.historicalChange.asOf', {
                            field: t('page.sportsmen.rankHistory.fields.' + change.field),
                            date: formatDate(change.asOf),
                            value: change.then || none,
                        })}
                    </div>
                    <div className={classes.line}>
                        {change.since
                            ? t('competition.historicalChange.nowSince', { value: change.now || none, date: formatDate(change.since) })
                            : t('competition.historicalChange.now', { value: change.now || none })}
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <Tooltip title={title}>
            <InfoIcon
                className={classes.icon}
                aria-label={t('competition.historicalChange.label')}
                data-testid='historical-change-mark'
            />
        </Tooltip>
    );
};

HistoricalChangeMark.propTypes = {
    changes: PropTypes.arrayOf(PropTypes.shape({
        field: PropTypes.string.isRequired,
        asOf: PropTypes.string,
        then: PropTypes.string,
        now: PropTypes.string,
        since: PropTypes.string,
    })),
};

HistoricalChangeMark.defaultProps = {
    changes: [],
};

export default HistoricalChangeMark;
